/**
 * CORE-527 — horizontal mirroring policy for live sessions.
 *
 * The bug: a master framed their shot against their own preview (kitchen
 * backsplash on the left, AC remote next to it) and viewers got the opposite
 * image. Nothing in the app asked for that — the behaviour was inherited from
 * whatever each SDK does by default, and the defaults disagree:
 *
 *   - Agora Web  — `LocalVideoTrack.play()` enables mirroring by default,
 *                  `RemoteVideoTrack.play()` disables it.
 *   - Agora native — the local canvas mirrors the front camera by default.
 *   - LiveKit native — `<VideoTrack mirror={isLocal}>`, mirrored on purpose.
 *   - LiveKit web — a raw <video> element, never mirrored.
 *
 * So the same master saw themselves mirrored on an Agora live and unmirrored
 * on a LiveKit one, for no reason either of them could see.
 *
 * The policy (owner's call, 2026-07-31): ONE image everywhere. The master, the
 * viewers and the server-side recording all show the camera's true output. A
 * master composes the frame — what is behind them, which side they lean to,
 * what they hold up — and must be able to trust that the audience sees what
 * they see. The alternative (mirror the viewer to match the self-view) would
 * reverse any text in frame and would still not match the recording, which is
 * captured from the published, unmirrored stream.
 *
 * Every render site imports from here rather than relying on an SDK default,
 * so the policy is one edit and `mirroredSelfView-CORE527.test.ts` fails the
 * build if a new surface goes back to trusting the default.
 */

/**
 * Whether a participant's own camera is flipped horizontally in their own
 * preview. `false` — see [[the module doc]] above. Flip to `true` to restore
 * the videoconferencing convention (Zoom/Meet mirror the self-view).
 *
 * Remote video is never mirrored under either setting: that is the stream as
 * published and as recorded.
 */
export const SELF_VIEW_MIRRORED = false;

/**
 * Playback config for an Agora **Web** local video track:
 * `localVideoTrack.play(container, AGORA_SELF_VIEW_PLAY_CONFIG)`.
 * Without the second argument the SDK mirrors — that omission is the bug.
 */
export const AGORA_SELF_VIEW_PLAY_CONFIG = { mirror: SELF_VIEW_MIRRORED } as const;

/**
 * Stamp the policy onto a raw <video> that LiveKit web handed us via
 * `track.attach()`. LiveKit doesn't mirror on its own, so this is currently a
 * no-op — it is here so the LiveKit path states the same intent as the Agora
 * one instead of merely happening to agree with it.
 *
 * Web-only by construction: called from the LiveKit attach sites, which never
 * run on native.
 */
export function applySelfViewMirror(element: { style?: { transform?: string } } | null | undefined): void {
  if (!element?.style) return;
  element.style.transform = SELF_VIEW_MIRRORED ? 'scaleX(-1)' : 'none';
}
